import {
  Alert,
  Button,
  DocumentIcon,
  EmptyState,
  Paper,
  Stack,
  UploadIcon,
} from '@all-manager/design-system-v2';
import type { KeyboardEvent, MouseEvent } from 'react';
import { useContractAiDropzone } from './useContractAiDropzone';

type Props = {
  onFileSelect: (file: File) => void;
  onSkipToManual: () => void;
};

export default function ContractAiDropzone(props: Props) {
  const hook = useContractAiDropzone({ onFileSelect: props.onFileSelect });

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      hook.handleTriggerFileInput();
    }
  }

  function handleButtonClick(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    hook.handleTriggerFileInput();
  }

  return (
    <Stack spacing={3}>
      {hook.validationError ? (
        <Alert severity="error">{hook.validationError}</Alert>
      ) : null}

      <div
        onDrop={hook.handleDrop}
        onDragOver={hook.handleDragOver}
        onDragLeave={hook.handleDragLeave}
        onClick={hook.handleTriggerFileInput}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        aria-label="Zone de dépôt du contrat"
        style={{ cursor: 'pointer' }}
      >
        <Paper>
          <EmptyState
            icon={hook.isDragOver ? <DocumentIcon fontSize="large" /> : <UploadIcon fontSize="large" />}
            title={hook.isDragOver ? 'Relâchez le document ici' : 'Déposez votre contrat ici'}
            subtitle="PDF ou image (scan, photo) · 18 Mo maximum. L'IA extrait automatiquement les dates, la tacite reconduction, le préavis et les missions cochées."
            action={(
              <Button
                variant="contained"
                size="small"
                startIcon={<UploadIcon />}
                onClick={handleButtonClick}
              >
                Choisir un fichier
              </Button>
            )}
          />
          <input
            ref={hook.fileInputRef}
            type="file"
            hidden
            accept="application/pdf,image/*"
            onChange={hook.handleFileInputChange}
          />
        </Paper>
      </div>

      <Stack alignItems="center">
        <Button
          variant="text"
          size="small"
          color="default"
          onClick={props.onSkipToManual}
        >
          Saisir manuellement sans document
        </Button>
      </Stack>
    </Stack>
  );
}
